/**
 * The one AudioContext the client owns.
 *
 * Browsers cap how many contexts a page may open, and every extra one
 * costs its own hardware output. Music and effects are the same speakers,
 * so they are the same graph — a module singleton rather than a context
 * per component.
 *
 * Autoplay policy shapes the whole design: a context created before the
 * player has interacted with the page starts `suspended`, and resuming it
 * early does not help. So creation is lazy and a one-shot gesture
 * listener resumes it the moment the browser would allow sound. Callers
 * never have to know: they build their nodes and the first gesture makes
 * them audible.
 */

import { log } from '../log'

let ctx: AudioContext | null = null
let unlocked = false

/** The shared context, or null when Web Audio is unavailable. */
export function audioContext(): AudioContext | null {
  if (ctx) return ctx

  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ??
    (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) {
    log.warn('audio.unavailable', 'Web Audio is missing, the game stays silent')
    return null
  }

  ctx = new Ctor()
  listenForGesture()
  return ctx
}

/** Resume on the first interaction of any kind, then stop listening. */
function listenForGesture(): void {
  if (unlocked) return
  const events: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'touchstart']
  const unlock = (): void => {
    unlocked = true
    for (const e of events) window.removeEventListener(e, unlock)
    void ctx?.resume().catch(() => {})
  }
  for (const e of events) window.addEventListener(e, unlock, { passive: true })
}

/**
 * Fetch and decode one file.
 *
 * Returns null instead of throwing: a missing sound must cost that sound
 * and nothing else. Sound is decoration — it can never take the board
 * down with it.
 */
export async function decodeAudio(url: string): Promise<AudioBuffer | null> {
  const context = audioContext()
  if (!context) return null
  try {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`${url} → ${res.status}`)
    return await context.decodeAudioData(await res.arrayBuffer())
  } catch (err) {
    log.warn('audio.decode-failed', 'this one sound stays silent', {
      url,
      reason: err instanceof Error ? err.message : String(err),
    })
    return null
  }
}
